import { useEffect, useState } from "react";

import ReactGA from "react-ga4";
import { useLocation } from "react-router";

import { useHomeRoute } from "@/routes/hooks/useHomeRoute";

import { useAuth } from "../../../context/Auth/AuthProvider";
import {
  getEnvironment,
  isAnalyticsCookieAccepted
} from "../../functions/functions";
import { ROUTES } from "../../types";
import useApiQueryParams from "../useApiQueryParams/useApiQueryParams";
import { grantAnalyticsConsent, revokeAnalyticsConsent } from "./analytics";

export const useGoogleAnalytics = (user: any) => {
  const { cookieConsentStatus } = useAuth();
  const { canAccessDashboard } = useHomeRoute();

  const { href } = globalThis.location;

  const location = useLocation();
  const { getApiQueryParams } = useApiQueryParams(location.pathname as ROUTES);
  const { isProductionEnv } = getEnvironment();

  const [initialized, setInitialized] = useState(false);
  const userExists = Boolean(user?.email);
  const fullPageUrl = getApiQueryParams();
  const trackingId = import.meta.env.VITE_GA_TRACKING_ID;

  const isReady =
    isProductionEnv &&
    Boolean(trackingId) &&
    isAnalyticsCookieAccepted(cookieConsentStatus) &&
    userExists;

  const hasAccessToNews = canAccessDashboard ? 1 : 0;

  // GTM container itself now loads conditionally from index.html.
  // This effect only updates Consent Mode state based on the banner choice.
  useEffect(() => {
    if (!isProductionEnv) return;

    if (isAnalyticsCookieAccepted(cookieConsentStatus)) {
      grantAnalyticsConsent();
    } else {
      revokeAnalyticsConsent();
    }
  }, [isProductionEnv, cookieConsentStatus]);

  useEffect(() => {
    if (isReady && !initialized) {
      ReactGA.initialize(trackingId);
      ReactGA.set({
        dimension1: user.id,
        hasAccessToNews
      });

      setInitialized(true);
    }
  }, [
    isProductionEnv,
    trackingId,
    cookieConsentStatus,
    href,
    initialized,
    userExists,
    user
  ]);

  useEffect(() => {
    if (initialized && userExists) {
      ReactGA.set({
        dimension3: fullPageUrl
      });

      ReactGA.send({ hitType: "pageview", page: fullPageUrl });
    }
  }, [initialized, userExists, user, location, fullPageUrl]);
};
